
import React from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { Euro, ArrowRight, Info, SkipForward, Zap, Activity, Plug } from 'lucide-react';

export const EnergyCostScreen = () => {
  const { data, updateData, nextStep, prevStep, skipStep } = useQuickscan();

  const handleCostChange = (value: number[]) => {
    updateData('monthlyEnergyCosts', value[0]);
  };

  const handlePvGenerationChange = (value: number[]) => {
    updateData('estimatedPvGeneration', value[0]);
  };

  const handleKwhUsageChange = (value: number[]) => {
    updateData('estimatedKwhUsage', value[0]);
  };

  const handleDeliveryCapacityChange = (value: number[]) => {
    updateData('connectionCapacityDelivery', value[0]);
  };

  const handleReturnCapacityChange = (value: number[]) => {
    updateData('connectionCapacityReturn', value[0]);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('nl-NL').format(num);
  };

  const yearlyAmount = data.monthlyEnergyCosts * 12;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Wat zijn je energiegegevens?
        </h2>
        <p className="text-gray-600">
          Dit helpt ons om je besparingspotentieel nauwkeuriger te berekenen
        </p>
        <div className="flex items-center justify-center mt-2 text-sm text-gray-500">
          <Info className="w-4 h-4 mr-1" />
          <span>Vul in wat je weet, de rest kun je overslaan</span>
        </div>
      </div>

      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-8 space-y-8">
        {/* Monthly Energy Costs */}
        <div>
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-4">
              <Euro className="w-8 h-8 text-energy-orange mr-2" />
              <span className="text-4xl font-bold text-energy-green">
                {formatCurrency(data.monthlyEnergyCosts)}
              </span>
              <span className="text-lg text-gray-600 ml-2">per maand</span>
            </div>
            <div className="text-xl text-gray-700">
              = {formatCurrency(yearlyAmount)} per jaar
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Gemiddelde maandelijkse energiekosten (exclusief transport)
            </label>
            <Slider
              value={[data.monthlyEnergyCosts]}
              onValueChange={handleCostChange}
              max={10000}
              min={500}
              step={100}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500 mt-2">
              <span>€500</span>
              <span>€10.000+</span>
            </div>
          </div>
        </div>

        {/* Estimated PV Generation */}
        <div className="border-t pt-6">
          <div className="text-center mb-4">
            <div className="flex items-center justify-center mb-2">
              <Zap className="w-6 h-6 text-energy-green mr-2" />
              <span className="text-2xl font-bold text-energy-green">
                {formatNumber(data.estimatedPvGeneration)} kWh
              </span>
              <span className="text-lg text-gray-600 ml-2">per jaar</span>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Geschatte PV opwek per jaar (indien van toepassing)
            </label>
            <Slider
              value={[data.estimatedPvGeneration]}
              onValueChange={handlePvGenerationChange}
              max={100000}
              min={0}
              step={1000}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500 mt-2">
              <span>0 kWh</span>
              <span>100.000+ kWh</span>
            </div>
          </div>
        </div>

        {/* Estimated kWh Usage */}
        <div className="border-t pt-6">
          <div className="text-center mb-4">
            <div className="flex items-center justify-center mb-2">
              <Activity className="w-6 h-6 text-energy-blue mr-2" />
              <span className="text-2xl font-bold text-energy-blue">
                {formatNumber(data.estimatedKwhUsage)} kWh
              </span>
              <span className="text-lg text-gray-600 ml-2">per jaar</span>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Geschat kWh verbruik per jaar
            </label>
            <Slider
              value={[data.estimatedKwhUsage]}
              onValueChange={handleKwhUsageChange}
              max={200000}
              min={5000}
              step={1000}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-500 mt-2">
              <span>5.000 kWh</span>
              <span>200.000+ kWh</span>
            </div>
          </div>
        </div>

        {/* Connection Capacities */}
        <div className="border-t pt-6 grid md:grid-cols-2 gap-6">
          {/* Delivery Capacity */}
          <div>
            <div className="text-center mb-4">
              <div className="flex items-center justify-center mb-2">
                <Plug className="w-6 h-6 text-energy-orange mr-2" />
                <span className="text-xl font-bold text-energy-orange">
                  {data.connectionCapacityDelivery} kW
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Aansluitvermogen levering
              </label>
              <Slider
                value={[data.connectionCapacityDelivery]}
                onValueChange={handleDeliveryCapacityChange}
                max={1000}
                min={10}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-2">
                <span>10 kW</span>
                <span>1000+ kW</span>
              </div>
            </div>
          </div>

          {/* Return Capacity */}
          <div>
            <div className="text-center mb-4">
              <div className="flex items-center justify-center mb-2">
                <Plug className="w-6 h-6 text-energy-green mr-2 transform rotate-180" />
                <span className="text-xl font-bold text-energy-green">
                  {data.connectionCapacityReturn} kW
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Aansluitvermogen teruglevering
              </label>
              <Slider
                value={[data.connectionCapacityReturn]}
                onValueChange={handleReturnCapacityChange}
                max={1000}
                min={0}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-2">
                <span>0 kW</span>
                <span>1000+ kW</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-4 mt-8 border-t pt-6">
          <div className="text-center p-4 bg-energy-green/5 rounded-lg">
            <div className="text-lg font-semibold text-energy-green">
              {formatCurrency(yearlyAmount * 0.2)}
            </div>
            <div className="text-sm text-gray-600">Mogelijk besparing/jaar</div>
          </div>
          <div className="text-center p-4 bg-energy-blue/5 rounded-lg">
            <div className="text-lg font-semibold text-energy-blue">
              {Math.round(yearlyAmount * 0.0004 * 10) / 10} ton
            </div>
            <div className="text-sm text-gray-600">CO2 reductie/jaar</div>
          </div>
          <div className="text-center p-4 bg-energy-orange/5 rounded-lg">
            <div className="text-lg font-semibold text-energy-orange">
              2-4 jaar
            </div>
            <div className="text-sm text-gray-600">Terugverdientijd</div>
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          Vorige
        </Button>
        
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={skipStep}
            className="border-gray-300 text-gray-600 hover:text-gray-800"
          >
            <SkipForward className="w-4 h-4 mr-1" />
            Overslaan
          </Button>
          
          <Button 
            onClick={nextStep}
            style={{ backgroundColor: '#014336' }}
            className="hover:opacity-90 text-white transition-opacity"
          >
            Volgende <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};
